import { supabase } from "@/integrations/supabase/client";
import { AssignmentSubmissionStatus } from "@/hooks/useAssignments";

export interface ProgressionCheckResult {
  canProgress: boolean;
  reason?: string;
  missingAssignments: { id: string; title: string; status: AssignmentSubmissionStatus | null }[];
}

/**
 * Check if user can progress past a module
 * Returns true only if all required assignments are passed
 */
export async function canProgressFromModule(
  userId: string,
  moduleId: string
): Promise<ProgressionCheckResult> {
  // Get all assignments in this module that gate progression (not optional)
  const { data: assignments, error: assignmentsError } = await supabase
    .from("assignments")
    .select("id, title, required, completion_requirement")
    .eq("module_id", moduleId)
    .eq("is_published", true)
    .neq("completion_requirement", "optional");

  if (assignmentsError) {
    console.error("[canProgressFromModule] Error fetching assignments:", assignmentsError);
    return { canProgress: false, reason: "Error checking assignments", missingAssignments: [] };
  }

  if (!assignments || assignments.length === 0) {
    return { canProgress: true, missingAssignments: [] };
  }

  // Get user's submissions for these assignments
  const assignmentIds = assignments.map((a: { id: string }) => a.id);
  const { data: submissions, error: submissionsError } = await supabase
    .from("assignment_submissions")
    .select("assignment_id, status")
    .eq("user_id", userId)
    .in("assignment_id", assignmentIds);

  if (submissionsError) {
    console.error("[canProgressFromModule] Error fetching submissions:", submissionsError);
    return { canProgress: false, reason: "Error checking submissions", missingAssignments: [] };
  }

  const statusMap = new Map<string, AssignmentSubmissionStatus>(
    submissions?.map((s: { assignment_id: string; status: string }) =>
      [s.assignment_id, s.status as AssignmentSubmissionStatus]
    ) || []
  );

  const missingAssignments: { id: string; title: string; status: AssignmentSubmissionStatus | null }[] = [];

  for (const assignment of assignments) {
    const status: AssignmentSubmissionStatus | null = statusMap.get(assignment.id) ?? null;
    const tier = (assignment as { completion_requirement?: string; required?: boolean }).completion_requirement
      ?? (assignment.required ? "review_required" : "optional");

    const satisfied =
      tier === "review_required"
        ? status === "passed"
        : tier === "submission_required"
          ? status === "submitted" || status === "passed" || status === "retry"
          : true;

    if (!satisfied) {
      missingAssignments.push({ id: assignment.id, title: assignment.title, status });
    }
  }

  if (missingAssignments.length > 0) {
    return {
      canProgress: false,
      reason: "You must satisfy all required assignments before moving to the next module.",
      missingAssignments,
    };
  }

  return { canProgress: true, missingAssignments: [] };
}

/**
 * Check if user has completed entire course
 */
export async function hasCourseCompleted(
  userId: string,
  courseId: string
): Promise<{ completed: boolean; reason?: string }> {
  // Get all modules in course
  const { data: modules, error: modulesError } = await supabase
    .from("modules")
    .select("id, title, sequence_order")
    .eq("course_id", courseId)
    .order("sequence_order", { ascending: true });

  if (modulesError || !modules) {
    return { completed: false, reason: "Error checking course modules" };
  }

  // Check each module
  for (const module of modules) {
    const result = await canProgressFromModule(userId, module.id);

    if (!result.canProgress) {
      return {
        completed: false,
        reason: `Module "${module.title}" is incomplete: ${result.reason}`,
      };
    }
  }

  return { completed: true };
}

/**
 * Get status label for assignment submission
 */
export function getAssignmentStatusLabel(status: AssignmentSubmissionStatus | null): string {
  switch (status) {
    case null:
      return "Not submitted";
    case "submitted":
      return "Awaiting review";
    case "passed":
      return "Passed";
    case "failed":
      return "Failed";
    case "retry":
      return "Needs revision";
    default:
      return "Unknown";
  }
}

/**
 * Get status color class for assignment submission
 */
export function getAssignmentStatusColor(status: AssignmentSubmissionStatus | null): string {
  switch (status) {
    case null:
      return "text-muted-foreground";
    case "submitted":
      return "text-blue-600 dark:text-blue-400";
    case "passed":
      return "text-green-600 dark:text-green-400";
    case "failed":
      return "text-destructive";
    case "retry":
      return "text-orange-600 dark:text-orange-400";
    default:
      return "text-muted-foreground";
  }
}
